import { Link } from 'react-router-dom';
import { Check, Circle, X } from 'lucide-react';
import { useState } from 'react';
import { useApi } from '../lib/useApi';
import { useT } from '../lib/i18n';

/**
 * What to do first, for a family that signed up ten seconds ago.
 *
 * The family home shows eight panels at once — heritage, languages, children,
 * members, quick links. For a family with history that is a dashboard. For a
 * family with nothing it is a wall with no door, and the one thing that gates
 * everything else (verified parental consent, without which no child can
 * exist) is a small card two thirds of the way down.
 *
 * So: the three steps that actually open the product, in order, with the next
 * one marked. It removes itself when they are done — permanent onboarding is
 * clutter — and can be dismissed early by a family who would rather explore.
 */
const DISMISSED = 'iran.firstSteps.dismissed.v1';

interface Progress {
  heritage: { stories: number; clips: number; people: number; events: number; generations: number };
}

// `childCount`, not `children`: React gives that name its own meaning, and a
// numeric `children` prop reads as a bug to everyone who meets it later.
export function FirstSteps({ childCount, members }: { childCount: number; members: number }) {
  const { t } = useT();
  const { data } = useApi<Progress>('/family/progress');
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED) === '1';
    } catch {
      return false;
    }
  });

  // Wait for progress rather than guess: showing "record your first story" to a
  // family who already has twelve is worse than showing nothing for a moment.
  if (hidden || !data) return null;

  const recordings = data.heritage.stories + data.heritage.clips;
  const steps = [
    { done: childCount > 0, key: 'firstSteps.child', to: '/consent', cta: 'firstSteps.childCta' },
    { done: recordings > 0, key: 'firstSteps.record', to: '/preserve', cta: 'firstSteps.recordCta' },
    { done: members > 1, key: 'firstSteps.invite', to: '/', cta: 'firstSteps.inviteCta' },
  ];
  if (steps.every((s) => s.done)) return null;

  const nextIndex = steps.findIndex((s) => !s.done);

  return (
    <section className="card stack first-steps" aria-labelledby="first-steps-heading">
      <div className="spread">
        <h2 id="first-steps-heading" style={{ margin: 0 }}>
          {t('firstSteps.title')}
        </h2>
        <button
          className="btn ghost small"
          onClick={() => {
            setHidden(true);
            try {
              localStorage.setItem(DISMISSED, '1');
            } catch {
              // Hidden for this visit only; not worth telling anyone about.
            }
          }}
        >
          <X size={15} strokeWidth={1.75} aria-hidden="true" /> {t('firstSteps.dismiss')}
        </button>
      </div>
      <p className="muted small" style={{ margin: 0 }}>
        {t('firstSteps.lead')}
      </p>
      <ol className="first-steps-list">
        {steps.map((s, i) => (
          <li key={s.key} className={s.done ? 'done' : i === nextIndex ? 'next' : ''}>
            <span className="mark" aria-hidden="true">
              {s.done ? <Check size={17} strokeWidth={2.2} /> : <Circle size={17} strokeWidth={1.9} />}
            </span>
            <span className="first-steps-text">
              <strong>{t(s.key)}</strong>
              {!s.done && i === nextIndex && (
                <Link className="btn small" to={s.to}>
                  {t(s.cta)}
                </Link>
              )}
            </span>
            {/* Screen readers get the state as words, not only as a drawn tick. */}
            <span className="sr-only">{s.done ? t('firstSteps.doneLabel') : t('firstSteps.todoLabel')}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
